import React from 'react';
import { 
  MessageSquare, 
  Heart, 
  Share2, 
  Eye, 
  Trash2, 
  EyeOff,
  AlertTriangle,
  Calendar,
  Image,
  Video
} from 'lucide-react';

const PublicationCard = ({ publication, onView, onDelete, onToggleVisibility }) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 60) return `Il y a ${diffInMinutes} min`;
    if (diffInMinutes < 1440) return `Il y a ${Math.floor(diffInMinutes / 60)}h`;
    if (diffInMinutes < 43200) return `Il y a ${Math.floor(diffInMinutes / 1440)}j`;
    
    return date.toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const getMediaIcon = (type) => {
    return type === 'image' ? <Image size={16} /> : <Video size={16} />;
  };

  const truncateText = (text, maxLength = 150) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const getHumeurEmoji = (humeur) => {
    const humeurs = {
      'HEUREUX': '😊',
      'TRISTE': '😢',
      'EXCITE': '🤩',
      'FATIGUE': '😩',
      'AMOUREUX': '😍',
      'EN_COLERE': '😠',
      'CELEBRER': '🎉',
      'NOSTALGIQUE': '🥺',
      'MALADE': '🤒',
      'SURPRIS': '😲',
      'COOL': '😎',
      'INQUIET': '😟'
    };
    return humeurs[humeur] || '';
  };

  return (
    <div className="bg-white rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 transform hover:-translate-y-1">
      {/* Header - Utilisateur */}
      <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FF6B00] to-[#437011] flex items-center justify-center text-white font-bold text-lg">
              {publication.utilisateur.first_name?.[0] || publication.utilisateur.username?.[0] || 'U'}
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">
                {publication.utilisateur.first_name && publication.utilisateur.last_name
                  ? `${publication.utilisateur.first_name} ${publication.utilisateur.last_name}`
                  : publication.utilisateur.username}
              </h3>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <Calendar size={12} />
                <span>{formatDate(publication.created_at)}</span>
                {publication.humeur && (
                  <>
                    <span>•</span>
                    <span>{getHumeurEmoji(publication.humeur)}</span>
                  </>
                )}
              </div>
            </div>
          </div>
          
          {/* Badges de statut */}
          <div className="flex items-center gap-2">
            {publication.est_masquee && (
              <span className="px-2 py-1 bg-red-100 text-red-600 text-xs rounded-full flex items-center gap-1">
                <EyeOff size={12} />
                Masquée
              </span>
            )}
            {publication.nombre_signalements > 0 && (
              <span className="px-2 py-1 bg-yellow-100 text-yellow-600 text-xs rounded-full flex items-center gap-1">
                <AlertTriangle size={12} />
                {publication.nombre_signalements} signalement{publication.nombre_signalements > 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Contenu de la publication */}
      <div className="p-4">
        {publication.texte && (
          <p 
            className="text-gray-700 mb-3 whitespace-pre-wrap"
            style={{ backgroundColor: publication.background_color !== '#FFFFFF' ? publication.background_color : 'transparent' }}
          >
            {truncateText(publication.texte)}
          </p>
        )}

        {/* Médias */}
        {publication.medias && publication.medias.length > 0 && (
          <div className="mb-3">
            <div className={`grid gap-2 ${
              publication.medias.length === 1 ? 'grid-cols-1' :
              publication.medias.length === 2 ? 'grid-cols-2' :
              publication.medias.length === 3 ? 'grid-cols-3' :
              'grid-cols-2'
            }`}>
              {publication.medias.slice(0, 4).map((media, index) => (
                <div key={media.id} className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden">
                  {media.type === 'image' ? (
                    <img 
                      src={`http://192.168.1.65:8000${media.fichier}`}
                      alt="Publication media" 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/300?text=Image+non+disponible';
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-200">
                      <Video size={32} className="text-gray-400" />
                    </div>
                  )}
                  {index === 3 && publication.medias.length > 4 && (
                    <div className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center text-white font-bold text-xl">
                      +{publication.medias.length - 4}
                    </div>
                  )}
                  <div className="absolute top-2 right-2 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-xs flex items-center gap-1">
                    {getMediaIcon(media.type)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Statistiques */}
        <div className="flex items-center gap-6 text-sm text-gray-600 pb-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Heart size={16} className="text-red-500" />
            <span className="font-medium">{publication.reactions_count || 0}</span>
          </div>
          <div className="flex items-center gap-2">
            <MessageSquare size={16} className="text-blue-500" />
            <span className="font-medium">{publication.commentaires_count || 0}</span>
          </div>
          <div className="flex items-center gap-2">
            <Share2 size={16} className="text-green-500" />
            <span className="font-medium">{publication.jaimes_count || 0}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 mt-3">
          <button
            onClick={() => onView(publication)}
            className="flex-1 px-4 py-2.5 bg-gradient-to-r from-[#FF6B00] to-[#e55d00] text-white rounded-lg hover:from-[#e55d00] hover:to-[#d04d00] transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2 text-sm font-medium"
          >
            <Eye size={16} />
            Voir détails
          </button>
          
          <button
            onClick={() => onToggleVisibility(publication)}
            className={`px-4 py-2.5 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2 text-sm font-medium ${
              publication.est_masquee
                ? 'bg-green-500 text-white hover:bg-green-600'
                : 'bg-yellow-500 text-white hover:bg-yellow-600'
            }`}
            title={publication.est_masquee ? "Afficher" : "Masquer"}
          >
            {publication.est_masquee ? <Eye size={16} /> : <EyeOff size={16} />}
          </button>
          
          <button
            onClick={() => onDelete(publication)}
            className="px-4 py-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2 text-sm font-medium"
            title="Supprimer"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PublicationCard;
